import React, { createContext, useContext } from "react";
import { DEFAULT_ISSUE_NAME, type ActivityNode, type NoteNode } from "./activity-types";
import type { Omittable } from "@salestrak/ui";

export type AttachmentsFocus = { label: string; ids: readonly string[] };

export type ActivityContextValue = Omittable<{
  activities: ActivityNode[];
  openAttachments?: (focus: AttachmentsFocus) => void;
  getNote: (noteId: string) => NoteNode | undefined;
  loadMoreActivities: () => Promise<boolean | undefined>;
  hasMoreActivities: boolean;
  loadingMore: boolean;
  /** Distinct YYYY-MM-DD days the issue has feed-visible activity on,
   *  sorted descending. Used by the jump-to-date popover to enable options
   *  and pick a target day without paginating. */
  activityDates: string[];
  scrollToNote?: (noteId: string, parentNoteId?: string | null) => Promise<void>;
  // Selected note tracking for scroll-to + highlight
  selectedNoteId: string | null;
  selectedParentId: string | null;
  registerNoteRef: (noteId: string, el: any) => void;
  /** The rendered card of a note the feed has mounted (a reply only once its
   *  thread is open) — the landing reads it to keep the new entry in view. */
  getNoteElement?: (noteId: string) => HTMLElement | null;
  /** Mark a note read once it has been seen (scrolled into view). */
  markNoteRead: (noteId: string) => void;
  createNote: (input: {
    note: string;
    issueId: string;
    parentNoteId?: string | null;
    mediaIds?: string[] | null;
    inlineMediaIds?: string[] | null;
  }) => Promise<any>;
  /** `mediaIds` is the complete ordered footer list; omit to leave it. */
  updateNote: (
    noteId: string,
    content: string,
    mediaIds?: string[] | null,
  ) => Promise<any>;
  deleteNote: (noteId: string) => Promise<any>;
  toggleReaction: (noteId: string, emoji: string) => Promise<void>;
  currentUserId: string | null;
  /** What the feed's own copy calls its subject ("created this account"). A
   *  feed belongs to exactly one entity, so the noun is a feed-level input,
   *  not a per-row one. Defaults to the Project vocabulary. */
  entityNoun?: string;
  focusActivityId?: string | null;
  focusNoteId?: string | null;
  clearFocusActivity?: () => void;
  /** Note ids (top-level and replies) that were unread when this view
   *  opened. A SNAPSHOT: marking a note read on scroll does not shrink it,
   *  so the "New" marker holds its place for the whole visit. */
  unreadAtOpen?: ReadonlySet<string>;
  /** The oldest note in `unreadAtOpen` — where the "New" marker sits. */
  firstUnreadNoteId?: string | null;
  /** Its parent when the first unread is a reply; the thread to expand. */
  firstUnreadParentId?: string | null;
  /** The view was opened to land on the first unread (the card's lit comment
   *  action) rather than at the bottom. */
  landOnUnread?: boolean;
}>;

const ActivityContext = createContext<ActivityContextValue | null>(null);

export function ActivityProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ActivityContextValue;
}) {
  return (
    <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>
  );
}

export function useActivityContext(
  componentName: string = "Component",
): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) {
    throw new Error(
      `[useActivityContext] ${componentName} must be within ActivityProvider`,
    );
  }
  return ctx;
}

export function useOptionalActivityContext(): ActivityContextValue | null {
  return useContext(ActivityContext);
}

/** The noun a chunk names the feed's subject with. Each call site casts it the
 *  way its own sentence reads; outside any provider it is a Project. */
export function useActivityNoun(): string {
  return useOptionalActivityContext()?.entityNoun ?? DEFAULT_ISSUE_NAME;
}
